import React from 'react';
import type { TimelineEntry, TimelineResponse } from '../../../types/biweeklyRotation';
import { formatPct } from '../../../types/biweeklyRotation';

interface HistoryTabProps {
  timeline: TimelineResponse | null;
  loading: boolean;
  onRefresh: () => void;
  onLoadMore: () => void;
}

const HistoryTab: React.FC<HistoryTabProps> = ({
  timeline,
  loading,
  onRefresh,
  onLoadMore,
}) => {
  if (loading && !timeline) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-neutral-400">Loading history...</div>
      </div>
    );
  }

  if (!timeline || timeline.timeline.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-white/10 bg-neutral-800/40 py-12 text-center">
        <svg
          className="w-12 h-12 mx-auto text-neutral-500 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="text-neutral-400 text-lg font-medium">No Rotation History</p>
        <p className="text-neutral-500 text-sm mt-1">
          Rotation history will appear here after executions
        </p>
        <button onClick={onRefresh} className="mt-4 btn btn-outline px-4 py-2" type="button">
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Rotation History</h3>
          <p className="text-sm text-neutral-400">
            {timeline.pagination.total} rotation{timeline.pagination.total !== 1 ? 's' : ''} recorded
          </p>
        </div>
        <button onClick={onRefresh} className="btn btn-outline px-4 py-2" type="button">
          Refresh
        </button>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Timeline Line */}
        <div className="absolute left-6 top-0 bottom-0 w-px bg-white/10" />

        {/* Timeline Entries */}
        <div className="space-y-4">
          {timeline.timeline.map((entry, idx) => (
            <TimelineEntryCard key={entry.id} entry={entry} isFirst={idx === 0} />
          ))}
        </div>
      </div>

      {/* Load More */}
      {timeline.pagination.hasMore && (
        <div className="flex justify-center pt-4">
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="btn btn-outline px-6 py-2 disabled:opacity-50"
            type="button"
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};

interface TimelineEntryCardProps {
  entry: TimelineEntry;
  isFirst: boolean;
}

const TimelineEntryCard: React.FC<TimelineEntryCardProps> = ({ entry, isFirst }) => {
  const [expanded, setExpanded] = React.useState(isFirst);

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'EXECUTED':
        return {
          bg: 'bg-emerald-500/20',
          text: 'text-emerald-100',
          border: 'border-emerald-500/30',
          dot: 'bg-emerald-400',
        };
      case 'SKIPPED':
        return {
          bg: 'bg-yellow-500/20',
          text: 'text-yellow-100',
          border: 'border-yellow-500/30',
          dot: 'bg-yellow-400',
        };
      case 'FAILED':
        return {
          bg: 'bg-red-500/20',
          text: 'text-red-100',
          border: 'border-red-500/30',
          dot: 'bg-red-400',
        };
      default:
        return {
          bg: 'bg-neutral-500/20',
          text: 'text-neutral-100',
          border: 'border-neutral-500/30',
          dot: 'bg-neutral-400',
        };
    }
  };

  const style = getStatusStyle(entry.status);

  return (
    <div className="relative pl-12">
      {/* Timeline Dot */}
      <div
        className={`absolute left-4 top-6 w-4 h-4 rounded-full ${style.dot} border-4 border-neutral-900`}
      />

      {/* Card */}
      <div
        className={`rounded-2xl border border-white/10 bg-neutral-800/60 overflow-hidden transition-all ${
          expanded ? '' : 'cursor-pointer hover:bg-neutral-800'
        }`}
        onClick={() => !expanded && setExpanded(true)}
      >
        {/* Header */}
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-lg font-semibold text-white">{entry.formattedDate}</p>
              <p className="text-sm text-neutral-400">
                {entry.summary.usersAffected} user{entry.summary.usersAffected !== 1 ? 's' : ''} rotated
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`badge text-xs px-2 py-1 ${style.bg} ${style.text} border ${style.border}`}>
              {entry.status}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              className="text-neutral-400 hover:text-white transition-colors"
              type="button"
            >
              <svg
                className={`w-5 h-5 transition-transform ${expanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Summary Stats (always visible) */}
        <div className="px-4 pb-4 grid grid-cols-4 gap-3">
          <div className="rounded-lg bg-neutral-900/50 p-2 text-center">
            <p className="text-xs text-neutral-400">Exits</p>
            <p className="text-lg font-semibold text-red-300">{entry.summary.totalExits}</p>
          </div>
          <div className="rounded-lg bg-neutral-900/50 p-2 text-center">
            <p className="text-xs text-neutral-400">Adds</p>
            <p className="text-lg font-semibold text-emerald-300">{entry.summary.totalAdds}</p>
          </div>
          <div className="rounded-lg bg-neutral-900/50 p-2 text-center">
            <p className="text-xs text-neutral-400">Turnover</p>
            <p className="text-lg font-semibold text-white">{formatPct(entry.summary.avgTurnoverPct)}</p>
          </div>
          <div className="rounded-lg bg-neutral-900/50 p-2 text-center">
            <p className="text-xs text-neutral-400">Cost</p>
            <p className="text-lg font-semibold text-white">{entry.summary.avgCostBps.toFixed(0)} bps</p>
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="border-t border-white/10 p-4 bg-neutral-900/30">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-neutral-400">Rotation ID</p>
                <p className="text-white font-mono text-xs">{entry.id}</p>
              </div>
              <div>
                <p className="text-neutral-400">Execution Date</p>
                <p className="text-white">{new Date(entry.date).toLocaleString()}</p>
              </div>
            </div>

            {/* Users Section */}
            {entry.users && entry.users.length > 0 && (
              <div className="mt-4">
                <p className="text-neutral-400 text-sm mb-2">Users Rotated</p>
                <div className="space-y-2">
                  {entry.users.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between rounded-lg bg-neutral-800/50 p-3"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                          <span className="text-cyan-400 text-sm font-medium">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-white font-medium text-sm">{user.name}</p>
                          <p className="text-neutral-500 text-xs">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex gap-4 text-xs">
                        <div className="text-center">
                          <p className="text-neutral-500">Exits</p>
                          <p className="text-red-300 font-semibold">{user.exits}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-neutral-500">Adds</p>
                          <p className="text-emerald-300 font-semibold">{user.adds}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-neutral-500">Turnover</p>
                          <p className="text-white font-semibold">{formatPct(user.turnoverPct)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-neutral-500">Cost</p>
                          <p className="text-white font-semibold">{user.costBps.toFixed(0)} bps</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {entry.status === 'EXECUTED' && (
              <div className="mt-4 flex items-center gap-2">
                <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-emerald-300 text-sm">Rotation completed successfully</span>
              </div>
            )}

            {entry.status === 'SKIPPED' && (
              <div className="mt-4 flex items-center gap-2">
                <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-yellow-300 text-sm">Rotation was skipped</span>
              </div>
            )}

            {entry.status === 'FAILED' && (
              <div className="mt-4 flex items-center gap-2">
                <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="text-red-300 text-sm">Rotation failed</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryTab;
